import React, { useState, useEffect, useCallback, Suspense, FC } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { PageSpinner, Toggle } from '@td-client-frontend/common';
import { Button } from '../../components/Button';
import { useJobDetails, useJobRefresh, useJobStatus, useSaveAndReprocess } from '../../hooks/useJob';
import { useDefaults } from '../../hooks/useDefaults';
import {
  TransformPackage,
  JobStatus,
  ReferenceData,
  TransformedData,
  EntityRow,
  DefaultEntitySchema,
  Overrides,
  Disabled
} from '../../@types/Templates';
import TemplateEntity from '../../components/TemplateEnitity/TemplateEntity';
import Modal from '../../components/Modal/NewModal';
import { TemplateEntityContextProvider } from '../../context/TemplateEntityContext';
interface JobTemplateEntitiesProps {
  jobDetails: TransformPackage;
  defaults:
    | {
        [x: string]: DefaultEntitySchema;
      }
    | undefined;
  overrides?: Overrides;
  disabled?: Disabled;
  onEntityChange: (template: any) => Promise<void | Error>;
  onDefaultsChange: (entityName: string, data: any) => Promise<void | Error>;
}

const TemplateEntities: FC<JobTemplateEntitiesProps> = ({
  jobDetails,
  defaults,
  overrides,
  disabled,
  onDefaultsChange,
  onEntityChange
}) => {
  const jobDetailsCsvData = Object.values(jobDetails?.CsvData);

  const referenceData: Omit<ReferenceData, 'entityType'> = jobDetailsCsvData.reduce(
    (acc: Omit<ReferenceData, 'entityType'>, item: TransformedData<EntityRow>) => {
      return {
        fields: {
          ...acc.fields,
          [item.type]: item.data.firstLine
        },
        rows: {
          ...acc.rows,
          [item.type]: item.data.firstDataLine
        }
      };
    },
    { fields: {}, rows: {} }
  );
  return (
    <>
      {jobDetailsCsvData.map(({ data, type }, index) => (
        <TemplateEntityContextProvider key={`${type}-provider`} referenceData={{ ...referenceData, entityType: type }}>
          <TemplateEntity
            key={type}
            defaults={defaults?.[type]}
            overrides={overrides}
            disabled={disabled}
            entityType={type}
            transformedData={data}
            template={jobDetails.Template}
            onEntityChange={onEntityChange}
            onDefaultsChange={onDefaultsChange}
            isLastEntity={jobDetailsCsvData.length - 1 === index}
          />
        </TemplateEntityContextProvider>
      ))}
    </>
  );
};

export const JobApprovalPage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { refresh, isLoading } = useJobRefresh(id);

  const { data: initialJobDetails } = useJobDetails(id);

  const [jobDetails, setJobDetails] = useState<TransformPackage>();

  const onEntityChange = useCallback(
    async (template) => {
      const refreshResult = await refresh(template);
      setJobDetails({ ...jobDetails, ...refreshResult });
    },
    [jobDetails, refresh, setJobDetails]
  );

  const { updateJobStatus } = useJobStatus(id);

  const { saveAndReprocess } = useSaveAndReprocess(id);
  const [isOpenSaveAndReprocessModal, setIsOpenSaveAndReprocessModal] = useState(false);

  const { defaults, overrides, disabled, onDefaultsChange, setTemplateDefaults } = useDefaults(
    jobDetails?.Template,
    onEntityChange
  );

  const handleHeaderToggle = useCallback(
    (value: boolean) => {
      const newTemplate = {
        ...jobDetails?.Template,
        config: { ...jobDetails?.Template.config, hasHeaders: value }
      };
      onEntityChange(newTemplate);
    },
    [jobDetails, onEntityChange]
  );

  useEffect(() => {
    if (!jobDetails) {
      setJobDetails(initialJobDetails);
      if (initialJobDetails?.Template?.defaults) {
        setTemplateDefaults(initialJobDetails.Template.defaults);
      }
    }
  }, [initialJobDetails, jobDetails, setTemplateDefaults]);

  const handleJobStatusUpdate = useCallback(
    (status: JobStatus) => async () => {
      try {
        await updateJobStatus(status);
        history.push('/reseller/jobs');
      } catch {}
    },
    [history, updateJobStatus]
  );

  const hasHeaders = jobDetails?.Template.config.hasHeaders;

  const onSaveAndReprocessModal = () => {
    setIsOpenSaveAndReprocessModal(!isOpenSaveAndReprocessModal);
  };

  const saveAndReprocessHandler = () => {
    setIsOpenSaveAndReprocessModal(false);
    saveAndReprocess(jobDetails?.Template);
    history.push('/reseller/jobs');
  };

  return (
    <>
      <Modal
        isOpen={isOpenSaveAndReprocessModal}
        onClose={onSaveAndReprocessModal}
        size="prompt"
        header=""
        actions={[
          { title: 'No', kind: 'secondary', onClick: onSaveAndReprocessModal },
          {
            title: 'Yes',
            kind: 'primary',
            autoClose: false,
            onClick: saveAndReprocessHandler
          }
        ]}
      >
        <div className="px-3 pb-3">
          <h2 className="font-effra text-black-default">
            Are you sure you would like to update the template and restart the job?
          </h2>
        </div>
      </Modal>

      <div className="flex flex-col flex-1 px-6 my-8">
        {/* header */}

        <div className="flex justify-between">
          <h1 className="text-4xl font-effra">Reseller Job Approval</h1>
          <div className="flex gap-6 items-center">
            <p>{`Template: ${jobDetails?.TemplateName}`}</p>
            <p>{`File: ${jobDetails?.JobZipFileName}`}</p>
            <label htmlFor="Toggle-hasHeaders" className="flex items-center gap-2">
              <span>Include headers</span>
              <Toggle name="hasHeaders" disabled={isLoading} value={hasHeaders} onChange={handleHeaderToggle} />
            </label>
          </div>
        </div>
        {/* main */}
        <div className="flex flex-col flex-1 mt-8">
          {jobDetails && (
            <TemplateEntities
              jobDetails={jobDetails}
              defaults={defaults}
              overrides={overrides}
              disabled={disabled}
              onEntityChange={onEntityChange}
              onDefaultsChange={onDefaultsChange}
            />
          )}
        </div>
        {/* footer */}
        <div
          className={clsx(
            'flex flex-col sm:flex-row sm:flex-wrap justify-end gap-4 fixed border-t-1 border-gray-20',
            'bottom-0 right-0 px-12 py-5 w-full bg-white z-30 pb-16 transition-all duration-150'
          )}
        >
          <Button title="Approve" kind="secondary" onClick={handleJobStatusUpdate(JobStatus.Approved)} />
          <Button title="Deny" kind="secondary" onClick={handleJobStatusUpdate(JobStatus.Denied)} />
          <Button title="Save and Reprocess" kind="secondary" onClick={onSaveAndReprocessModal} />
        </div>
      </div>
    </>
  );
};

const JobApprovalPageWithSuspense = () => (
  <Suspense fallback={<PageSpinner />}>
    <JobApprovalPage />
  </Suspense>
);

export default JobApprovalPageWithSuspense;
